import { useCallback, useState } from 'react';

import { useSelector } from 'react-redux';

import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  NativeSelect,
  OutlinedInput,
  Paper,
  Theme,
  Typography,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { get as _get } from 'lodash';

import { getMakes, getModels } from '../../../api/vehicleApi';
import { getAuthKey, getSessionId, getUrlKey } from '../../../app/store/combinedSelectors';
import {
  DeleteConfirmationConstants,
  deleteThisContactBtn,
  FNOLCancelConfirmationConstants,
  GenericModalTopMargin,
} from '../../../Constants/Constants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import { ThirdPartyInfoInterface } from '../../../types/type';
import {
  ADDRESS_REGEX,
  CITY_REGEX,
  FIRST_NAME_REGEX,
  LAST_NAME_REGEX,
  ZIP_CODE_REGEX,
} from '../../../utils/Regex-Util';
import { getValidVehicleYears, postMessageToParentApp, usStates } from '../../../utils/Utilities';
import Accordion from '../../shared/Accordion';
import EmailInput from '../../shared/EmailInput/EmailInput';
import GenericModal from '../../shared/GenericModal/GenericModal';
import PhoneInput from '../../shared/PhoneInput/PhoneInput';
import { RegexTextField } from '../../shared/RegexTextField/RegexTextField';

export interface ThirdPartyInfoCollectionComponentInterface {
  id: number;
  theme: Theme;
  contact: ThirdPartyInfoInterface;
  contactInfoUpdateHandler: (contact: ThirdPartyInfoInterface) => void;
  deleteContactHandler: (contact: ThirdPartyInfoInterface) => void;
}

export default function ThirdPartyInfoCollectionComponent(props: ThirdPartyInfoCollectionComponentInterface) {
  const [accordionExpanded, setAccordionExpanded] = useState(true);
  const years = getValidVehicleYears();
  const urlKey = useSelector(getUrlKey);
  const sessionId = useSelector(getSessionId);
  const authKey = useSelector(getAuthKey);
  const [makesList, setMakesList] = useState<string[]>([props.contact.vehicleMake ? props.contact.vehicleMake : '']);
  const [selectedMakesList, setSelectedMakesList] = useState<string[]>([]);
  const [modelsList, setModelsList] = useState<string[]>([
    props.contact.vehicleModel ? props.contact.vehicleModel : '',
  ]);
  const [selectedModelsList, setSelectedModelsList] = useState<string[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const isMobile = useMediaQuery(props.theme.breakpoints.down('sm'));

  const updateParentOnAccordionChange = useCallback((activeStateText: string) => {
    if (activeStateText.length) {
      setAccordionExpanded(true);
    } else {
      setAccordionExpanded(false);
    }
  }, []);

  function emailValid(validEmail: boolean) {
    if (props.contact.emailAddressValid !== validEmail) {
      props.contactInfoUpdateHandler({
        ...props.contact,
        emailAddressValid: validEmail,
      });
    }
  }

  function phoneValid(validPhone: any) {
    if (props.contact.phoneNumberValid !== validPhone) {
      props.contactInfoUpdateHandler({
        ...props.contact,
        phoneNumberValid: validPhone,
      });
    }
  }

  function handleFirstNameChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    props.contactInfoUpdateHandler({
      ...props.contact,
      firstName: _get(event, 'target.value'),
    });
  }

  function handleLastNameChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    props.contactInfoUpdateHandler({
      ...props.contact,
      lastName: _get(event, 'target.value'),
    });
  }

  function handleInjuryChange() {
    props.contactInfoUpdateHandler({
      ...props.contact,
      personInjured: !props.contact.personInjured,
    });
  }

  function handleEmailChange(email: any) {
    props.contactInfoUpdateHandler({
      ...props.contact,
      emailAddress: email,
    });
  }

  function handlePhoneChange(phoneNumber: any) {
    props.contactInfoUpdateHandler({
      ...props.contact,
      phoneNumber: phoneNumber,
    });
  }

  function handleVehicleInvolvedChange() {
    props.contactInfoUpdateHandler({
      ...props.contact,
      vehicleInvolved: !props.contact.vehicleInvolved,
    });
  }

  async function handleYearChange(event: any) {
    const selectedYear: string = _get(event, 'target.value', '-1');
    if (props.contact.vehicleYear !== selectedYear) {
      props.contactInfoUpdateHandler({
        ...props.contact,
        vehicleYear: selectedYear,
      });
    }
    setMakesList([...selectedMakesList]);
    setModelsList([...selectedModelsList]);
    if (!selectedYear) {
      return;
    }
    const makes = await getMakes(urlKey, sessionId, authKey, selectedYear);
    setMakesList(makes);
  }

  async function handleMakeChange(event: any) {
    const selectedMake: string = _get(event, 'target.value', '-1');
    if (props.contact.vehicleMake !== selectedMake) {
      props.contact.vehicleMake = selectedMake;
      props.contactInfoUpdateHandler({
        ...props.contact,
        vehicleMake: selectedMake,
      });
      const models = await getModels(urlKey, sessionId, authKey, props.contact.vehicleYear ?? '', selectedMake);
      setSelectedMakesList([...selectedMakesList, selectedMake]);
      setModelsList([...selectedModelsList, ...models]);
    }
  }

  function handleModelChange(event: any) {
    const selectedModel: string = _get(event, 'target.value', '-1');
    if (props.contact.vehicleModel !== selectedModel) {
      props.contactInfoUpdateHandler({
        ...props.contact,
        vehicleModel: selectedModel,
      });
      setSelectedModelsList([...selectedModelsList, selectedModel]);
    }
  }

  function handleAddressLine1Change(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const newAddressLine1 = _get(event, 'target.value');
    if (props.contact.addressLine1 !== newAddressLine1) {
      props.contactInfoUpdateHandler({
        ...props.contact,
        addressLine1: newAddressLine1,
      });
    }
  }

  function handleAddressLine2Change(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const newAddressLine2 = _get(event, 'target.value');
    if (props.contact.addressLine2 !== newAddressLine2) {
      props.contactInfoUpdateHandler({
        ...props.contact,
        addressLine2: newAddressLine2,
      });
    }
  }

  function handleCityChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const newCity = _get(event, 'target.value');
    if (props.contact.city !== newCity) {
      props.contactInfoUpdateHandler({
        ...props.contact,
        city: newCity,
      });
    }
  }

  function handleStateChange(event: any) {
    const newState = _get(event, 'target.value');
    if (props.contact.state !== newState) {
      props.contactInfoUpdateHandler({
        ...props.contact,
        state: newState,
      });
    }
  }

  function handleZipChange(event: any) {
    const newZip = _get(event, 'target.value');
    if (props.contact.zipCode !== newZip) {
      props.contactInfoUpdateHandler({
        ...props.contact,
        zipCode: newZip,
      });
    }
  }

  function openDeleteModal() {
    if (isMobile) {
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_SCROLL);
    }
    // Set contact to delete
    setDeleteModalOpen(true);
  }

  function deleteConfirmation() {
    props.deleteContactHandler(props.contact);
    setDeleteModalOpen(false);
  }

  function handleCloseButtonClick() {
    setDeleteModalOpen(false);
  }

  function handleConfirmedDelete() {
    deleteConfirmation();
  }

  const deleteModal = (
    <GenericModal
      title={'Delete this contact?'}
      description={FNOLCancelConfirmationConstants.CONTACT_INFO_DELETE_DESCRIPTION}
      boldText="Delete"
      primaryButtonText={DeleteConfirmationConstants.PRIMARY_BUTTON_TEXT}
      secondaryButtonText={DeleteConfirmationConstants.SECONDARY_BUTTON_TEXT}
      alertBoxStyle={props.theme.custom.styles.genericModalDestructiveAlertBoxStyle}
      verticalPositionForMobile={GenericModalTopMargin}
      theme={props.theme}
      closeButtonActionHandler={handleCloseButtonClick}
      primaryButtonActionHandler={handleConfirmedDelete}
      secondaryButtonActionHandler={handleCloseButtonClick}
    ></GenericModal>
  );

  const thirdPartyInfoCollectionComponent = (
    <Grid item xs={12} sx={props.theme.custom.styles.accordionRowGridItemStyle}>
      <Paper
        variant={props.theme.custom.styles.accordionRowHeader.variant as any} // Why 'any'? For more, refer => https://github.com/mui/material-ui/issues/15697
        elevation={props.theme.custom.styles.accordionRowHeader.elevation}
        sx={{
          p: 0.1,
          borderRadius: props.theme.custom.styles.borderRadius.radiusDefault,
          borderColor: props.theme.custom.styles.showBorderAroundListItems
            ? props.theme.custom.colors.defaultBorderColor
            : props.theme.custom.colors.bodyColor,
        }}
      >
        <Accordion
          updateParentOnAccordionChange={updateParentOnAccordionChange}
          title={(props.contact.firstName + ' ' + props.contact.lastName).trim() || 'Add Person ' + (props.id + 1)}
          toggleOpen={true}
          toggleClose={false}
          component={
            <Grid
              container
              sx={{
                mx: '1rem',
                pr: '2rem',
              }}
            >
              <Grid
                sx={{
                  marginTop: '1em',
                  marginBottom: '1em',
                }}
                item
                container
                spacing={2}
              >
                <Grid container item md={6} xs={12}>
                  <Grid item xs={12}>
                    <Typography sx={props.theme.custom.styles.formLabelStyle}>First Name</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <RegexTextField
                      key={'firstName-' + props.contact.id}
                      id={'firstName-' + props.contact.id}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      size="small"
                      value={props.contact.firstName}
                      regex={FIRST_NAME_REGEX}
                      changeHandler={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleFirstNameChange(event)
                      }
                      inputProps={{
                        step: 300, // 5 min
                      }}
                      sx={{ width: '100%' }}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
                <Grid container item md={6} xs={12}>
                  <Grid item xs={12}>
                    <Typography sx={props.theme.custom.styles.formLabelStyle}>Last Name</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <RegexTextField
                      id="lastName"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      size="small"
                      value={props.contact.lastName}
                      regex={LAST_NAME_REGEX}
                      changeHandler={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleLastNameChange(event)
                      }
                      inputProps={{
                        step: 300, // 5 min
                      }}
                      sx={{ width: '100%' }}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid sx={{ marginTop: '1em', marginBottom: '1em' }} item xs={12}>
                <FormControlLabel
                  label={
                    <Typography
                      sx={{
                        ...props.theme.custom.styles.formTextStyle,
                      }}
                    >
                      This person was injured.
                    </Typography>
                  }
                  control={
                    <Checkbox
                      defaultChecked={props.contact.personInjured}
                      value={props.contact.personInjured}
                      onClick={() => handleInjuryChange()}
                      name={'injured'}
                      disableRipple
                      sx={{
                        color: props.theme.custom.styles.checkBox.defaultBorder,
                        '&.Mui-checked': {
                          color: props.theme.custom.styles.checkBox.selectedCheckmarkColor,
                        },
                      }}
                    />
                  }
                  key={'injured'}
                  tabIndex={1}
                  sx={{ width: '100%' }}
                />
              </Grid>
              <Grid item container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <EmailInput
                    width={'100%'}
                    margin={'0%'}
                    isRequired={false}
                    hideSubCase={false}
                    emailValid={emailValid}
                    defaultValue={props.contact.emailAddress}
                    emailValueUpdate={(email: string) => handleEmailChange(email)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PhoneInput
                    isRequired={false}
                    width={'100%'}
                    margin={'0%'}
                    hideSubCase={false}
                    phoneValid={phoneValid}
                    defaultValue={props.contact.phoneNumber}
                    phoneValueUpdate={(phone: string) => handlePhoneChange(phone)}
                  />
                </Grid>
              </Grid>

              <Grid sx={{ marginTop: '1em', marginBottom: '1em' }} item xs={12}>
                <FormControlLabel
                  label={
                    <Typography
                      sx={{
                        ...props.theme.custom.styles.formTextStyle,
                      }}
                    >
                      A vehicle was involved.
                    </Typography>
                  }
                  control={
                    <Checkbox
                      defaultChecked={props.contact.vehicleInvolved}
                      value={props.contact.vehicleInvolved}
                      onChange={(event) => handleVehicleInvolvedChange()}
                      name={'vehicleInvolved'}
                      disableRipple
                      sx={{
                        color: props.theme.custom.styles.checkBox.defaultBorder,
                        '&.Mui-checked': {
                          color: props.theme.custom.styles.checkBox.selectedCheckmarkColor,
                        },
                      }}
                    />
                  }
                  key={'vehicleInvolved'}
                  tabIndex={1}
                  sx={{ width: '100%' }}
                />
              </Grid>

              {props.contact.vehicleInvolved && (
                <Grid container spacing={2}>
                  <Grid sx={{ marginTop: '1em' }} item xs={12} sm={4}>
                    <Grid item xs={12}>
                      <Typography sx={props.theme.custom.styles.formLabelStyle}>Vehicle Year</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <NativeSelect
                          value={props.contact.vehicleYear}
                          input={<OutlinedInput size="small" label="year" />}
                          onChange={(event) => handleYearChange(event)}
                          inputProps={{
                            name: 'year',
                            id: 'uncontrolled-native',
                          }}
                        >
                          <option value=""></option>
                          {years &&
                            years.map((year: number) => (
                              <option value={year} key={year}>
                                {year}
                              </option>
                            ))}
                        </NativeSelect>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Grid sx={{ marginTop: '1em' }} item xs={12} sm={4}>
                    <Grid item xs={12}>
                      <Typography sx={props.theme.custom.styles.formLabelStyle}>Vehicle Make</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <NativeSelect
                          value={props.contact.vehicleMake}
                          input={<OutlinedInput size="small" label="make" />}
                          onChange={(event) => handleMakeChange(event)}
                          inputProps={{
                            name: 'age',
                            id: 'uncontrolled-native',
                          }}
                        >
                          <option value=""></option>
                          {makesList &&
                            makesList.map((make: string) => (
                              <option value={make} key={make}>
                                {make}
                              </option>
                            ))}
                        </NativeSelect>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Grid sx={{ marginTop: '1em' }} item xs={12} sm={4}>
                    <Grid item xs={12}>
                      <Typography sx={props.theme.custom.styles.formLabelStyle}>Vehicle Model</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <NativeSelect
                          value={props.contact.vehicleModel}
                          input={<OutlinedInput size="small" label="model" />}
                          onChange={(event) => handleModelChange(event)}
                          inputProps={{
                            name: 'age',
                            id: 'uncontrolled-native',
                          }}
                        >
                          <option value=""></option>
                          {modelsList &&
                            modelsList.map((model: string) => (
                              <option value={model} key={model}>
                                {model}
                              </option>
                            ))}
                        </NativeSelect>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
              )}

              <Grid sx={{ marginTop: '1em', marginBottom: '1em' }} item xs={12}>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography sx={props.theme.custom.styles.formLabelStyle}>Address Line 1</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <RegexTextField
                      id="addressLine1"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      size="small"
                      value={props.contact.addressLine1}
                      regex={ADDRESS_REGEX}
                      changeHandler={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleAddressLine1Change(event)
                      }
                      inputProps={{
                        step: 300, // 5 min
                      }}
                      sx={{ width: '100%' }}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid sx={{ marginTop: '.5em', marginBottom: '1em' }} item xs={12}>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography sx={props.theme.custom.styles.formLabelStyle}>Address Line 2</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <RegexTextField
                      id="addressLine2"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      size="small"
                      regex={ADDRESS_REGEX}
                      value={props.contact.addressLine2}
                      changeHandler={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleAddressLine2Change(event)
                      }
                      inputProps={{
                        step: 300, // 5 min
                      }}
                      sx={{ width: '100%' }}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid
                sx={{
                  marginBottom: '1em',
                }}
                item
                container
                spacing={2}
              >
                <Grid item container md={4} xs={12}>
                  <Grid item xs={12}>
                    <Typography sx={props.theme.custom.styles.formLabelStyle}>City</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <RegexTextField
                      id="city"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      size="small"
                      value={props.contact.city}
                      regex={CITY_REGEX}
                      changeHandler={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleCityChange(event)
                      }
                      inputProps={{
                        step: 300, // 5 min
                      }}
                      sx={{ width: '100%' }}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
                <Grid container item md={4} xs={12}>
                  <Grid item xs={12}>
                    <Typography sx={props.theme.custom.styles.formLabelStyle}>State</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <NativeSelect
                        value={props.contact.state}
                        input={<OutlinedInput size="small" label="state" />}
                        onChange={(event) => handleStateChange(event)}
                        variant="outlined"
                        inputProps={{
                          name: 'state',
                          id: 'uncontrolled-native',
                        }}
                        sx={{ width: '100%' }}
                      >
                        <option value=""></option>
                        {usStates &&
                          usStates.map((state: { value: string; name: string }) => (
                            <option value={state.value} key={state.value}>
                              {state.name}
                            </option>
                          ))}
                      </NativeSelect>
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid container item md={4} xs={12}>
                  <Grid item xs={12}>
                    <Typography sx={props.theme.custom.styles.formLabelStyle}>Zip Code</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <RegexTextField
                      id="zip"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      size="small"
                      value={props.contact.zipCode}
                      regex={ZIP_CODE_REGEX}
                      changeHandler={(event) => handleZipChange(event)}
                      inputProps={{
                        step: 300, // 5 min
                      }}
                      sx={{ width: '100%' }}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid
                sx={{
                  marginTop: '.5em',
                  marginBottom: '1em',
                }}
                item
                xs={12}
              >
                <Button
                  onClick={() => openDeleteModal()}
                  sx={{
                    ...props.theme.custom.styles.genericDestructiveTertiaryButton,
                    minWidth: 'max-content',
                  }}
                >
                  {deleteThisContactBtn}
                </Button>
              </Grid>
            </Grid>
          }
        />
      </Paper>
    </Grid>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {!!deleteModalOpen && deleteModal}
      {thirdPartyInfoCollectionComponent}
    </Box>
  );
}
